import React, { useEffect, useState, useRef } from 'react';

const POLLING_INTERVAL = 4000;

function formatCountdown(ms) {
    if (ms <= 0) return '00:00';
    const totalSec = Math.floor(ms / 1000);
    const min = Math.floor(totalSec / 60);
    const sec = totalSec % 60;
    return `${String(min).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
}

export default function AjustesPage() {
    const [estado, setEstado] = useState(null); // { vinculado, username }
    const [codigo, setCodigo] = useState(null); // { codigo, expires_at }
    const [cargando, setCargando] = useState(false);
    const [error, setError] = useState(null);
    const [countdown, setCountdown] = useState(0);
    const [copiado, setCopiado] = useState(false);
    const pollingRef = useRef(null);
    const timerRef = useRef(null);

    const fetchEstado = async () => {
        try {
            const res = await fetch(`${window.REACT_APP_API_ENDPOINT}/api/agent/telegram/estado`, { credentials: 'include' });
            if (!res.ok) return;
            const data = await res.json();
            setEstado(data);
            if (data.vinculado) {
                setCodigo(null);
                stopPolling();
            }
        } catch {
            // silencioso
        }
    };

    const startPolling = () => {
        if (pollingRef.current) return;
        pollingRef.current = setInterval(fetchEstado, POLLING_INTERVAL);
    };

    const stopPolling = () => {
        if (pollingRef.current) {
            clearInterval(pollingRef.current);
            pollingRef.current = null;
        }
    };

    useEffect(() => {
        fetchEstado();
        return () => {
            stopPolling();
            if (timerRef.current) clearInterval(timerRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!codigo) {
            if (timerRef.current) clearInterval(timerRef.current);
            return;
        }
        const expiresAt = new Date(codigo.expires_at).getTime();
        const tick = () => {
            const remaining = expiresAt - Date.now();
            setCountdown(Math.max(0, remaining));
            if (remaining <= 0) {
                clearInterval(timerRef.current);
                setCodigo(null);
                stopPolling();
            }
        };
        tick();
        timerRef.current = setInterval(tick, 1000);
        return () => clearInterval(timerRef.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [codigo]);

    const handleObtenerCodigo = async () => {
        setCargando(true);
        setError(null);
        try {
            const res = await fetch(`${window.REACT_APP_API_ENDPOINT}/api/agent/telegram/codigo`, { credentials: 'include' });
            if (!res.ok) throw new Error('Error al obtener el código');
            const data = await res.json();
            setCodigo(data);
            startPolling();
        } catch (e) {
            setError(e.message);
        } finally {
            setCargando(false);
        }
    };

    const handleDesconectar = async () => {
        setCargando(true);
        setError(null);
        try {
            const res = await fetch(`${window.REACT_APP_API_ENDPOINT}/api/agent/telegram/desvincular`, {
                method: 'DELETE',
                credentials: 'include',
            });
            if (!res.ok) throw new Error('Error al desconectar');
            setEstado({ vinculado: false, username: null });
            setCodigo(null);
        } catch (e) {
            setError(e.message);
        } finally {
            setCargando(false);
        }
    };

    return (
        <div className="max-w-2xl mx-auto mb-8">
            <h1 className="text-2xl font-bold text-gray-800 mb-6">Ajustes</h1>

            <div className="bg-white rounded-xl shadow p-6">
                <div className="flex items-center gap-3 mb-4">
                    {/* Telegram logo SVG */}
                    <svg className="w-8 h-8 text-[#229ED9]" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.562 8.248-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.447 1.394c-.16.16-.295.295-.605.295l.213-3.053 5.56-5.023c.242-.213-.054-.333-.373-.12l-6.871 4.326-2.962-.924c-.643-.204-.657-.643.136-.953l11.57-4.461c.537-.194 1.006.131.833.941z" />
                    </svg>
                    <h2 className="text-lg font-semibold text-gray-700">Telegram</h2>
                </div>

                {error && (
                    <p className="text-red-600 text-sm mb-4 bg-red-50 border border-red-200 rounded px-3 py-2">
                        {error}
                    </p>
                )}

                {estado === null ? (
                    <p className="text-gray-500 text-sm">Cargando...</p>
                ) : estado.vinculado ? (
                    <div>
                        <div className="flex items-center gap-2 mb-4">
                            <span className="text-green-600 font-medium">✅ Telegram conectado</span>
                            {estado.username && (
                                <span className="text-gray-500 text-sm">(@{estado.username})</span>
                            )}
                        </div>
                        <p className="text-gray-600 text-sm mb-5">
                            Puedes chatear con el asistente directamente desde Telegram.
                        </p>
                        <button
                            onClick={handleDesconectar}
                            disabled={cargando}
                            className="px-4 py-2 bg-red-100 text-red-700 rounded-lg text-sm font-medium hover:bg-red-200 disabled:opacity-50 transition-colors"
                        >
                            {cargando ? 'Desconectando...' : 'Desconectar Telegram'}
                        </button>
                    </div>
                ) : (
                    <div>
                        <p className="text-gray-600 text-sm mb-5">
                            Conecta tu cuenta de Telegram para chatear con el asistente desde móvil sin abrir la web.
                        </p>

                        {codigo ? (
                            <div className="mb-5">
                                <p className="text-gray-700 text-sm mb-2">Tu código de vinculación:</p>
                                <div className="font-mono text-4xl font-bold tracking-widest text-center text-blue-700 bg-blue-50 rounded-xl py-4 px-6 mb-3 select-all">
                                    {codigo.codigo}
                                </div>
                                <p className="text-center text-sm text-gray-500 mb-4">
                                    Expira en{' '}
                                    <span className={`font-mono font-semibold ${countdown < 60000 ? 'text-red-500' : 'text-gray-700'}`}>
                                        {formatCountdown(countdown)}
                                    </span>
                                </p>
                                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm text-gray-700 space-y-1">
                                    <p className="font-medium">Instrucciones:</p>
                                    <ol className="list-decimal list-inside space-y-1">
                                        <li>Abre Telegram y busca <span className="font-mono font-semibold">@reformistabot</span></li>
                                        <li>
                                            Envía el comando:{' '}
                                            <span className="font-mono font-semibold">/vincular {codigo.codigo}</span>
                                            <button
                                                onClick={() => {
                                                    navigator.clipboard.writeText(`/vincular ${codigo.codigo}`);
                                                    setCopiado(true);
                                                    setTimeout(() => setCopiado(false), 2000);
                                                }}
                                                title="Copiar al portapapeles"
                                                className="ml-2 align-middle transition-colors"
                                            >
                                                {copiado ? (
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="inline h-4 w-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                                    </svg>
                                                ) : (
                                                    <svg xmlns="http://www.w3.org/2000/svg" className="inline h-4 w-4 text-gray-400 hover:text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-4 12h6a2 2 0 002-2v-8a2 2 0 00-2-2h-6a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                                    </svg>
                                                )}
                                            </button>
                                        </li>
                                        <li>Esta página se actualizará automáticamente</li>
                                    </ol>
                                </div>
                            </div>
                        ) : (
                            <button
                                onClick={handleObtenerCodigo}
                                disabled={cargando}
                                className="px-5 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors"
                            >
                                {cargando ? 'Generando código...' : 'Obtener código de vinculación'}
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
